import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Space, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { signIn } from 'aws-amplify/auth';
import { setLoading, setAuthenticated, setUser, setError } from '../store/slices/authSlice';
import { userApi } from '../services/userService';
import { setProfile } from '../store/slices/userSlice';
import { authService } from '../services/authService';

const { Title, Text } = Typography;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.auth);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const { username, password } = values;

      // Sign in with Cognito
      const signInResult = await signIn({ username, password });

      if (signInResult.isSignedIn) {
        // Get user attributes
        const userAttributes = await signInResult.nextStep.signInStep;
        const userDetails = await userApi.getUser();

        dispatch(setProfile(userDetails));
        dispatch(setUser({
          username: username,
          attributes: userAttributes,
        }));
        dispatch(setAuthenticated(true));

        message.success('Login successful!');
        navigate('/clients');
      }
    } catch (error) {
      console.error('Login error:', error);
      await authService.signOut();

      let errorMessage = 'Login failed. Please try again.';

      if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Invalid username or password.';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'Please confirm your account before signing in.';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'User not found.';
      } else if (error.name === 'TooManyRequestsException') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }

      dispatch(setError(errorMessage));
      message.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            Admin Dashboard
          </Title>
          <Text type="secondary">Sign in to your account</Text>
        </div>

        {error && (
          <div style={{
            marginBottom: 16,
            padding: '8px 12px',
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '6px',
            color: '#cf1322'
          }}>
            {error}
          </div>
        )}

        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: 'Please input your username!' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Username"
              size="large"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              style={{ height: 45 }}
              loading={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
